import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Home, ArrowLeft } from "lucide-react";
import { SEO } from '@/components/SEO';
import { SITE_NAME, SITE_ORIGIN } from '@/seo/siteConfig';

const NotFound = () => {
  return (
    <>
      <SEO 
        title="Pagina niet gevonden"
        description="De pagina die je zoekt bestaat niet. Ga terug naar Organisatieopstellingen voor systemisch werk, organisatieopstellingen en teamcoaching."
        canonical={`${SITE_ORIGIN}/404`}
        noindex={true}
        schemaOrg={{
          organization: {
            '@type': 'Organization',
            name: SITE_NAME,
            url: SITE_ORIGIN,
          }
        }}
      />
      
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Pagina niet gevonden (404)',
            description: 'De pagina die je zoekt bestaat niet. Ga terug naar Organisatieopstellingen.',
            url: `${SITE_ORIGIN}/404`,
            mainEntity: {
              '@type': 'Organization',
              name: SITE_NAME,
              url: SITE_ORIGIN,
            }
          })
        }}
      />
      
      <div className="min-h-screen bg-gradient-subtle relative overflow-hidden flex items-center justify-center px-4">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 w-32 h-32 rounded-full bg-primary/20 animate-float"></div>
          <div className="absolute top-40 right-20 w-24 h-24 rounded-full bg-secondary/20 animate-float" style={{animationDelay: '1s'}}></div>
          <div className="absolute bottom-20 left-1/4 w-40 h-40 rounded-full bg-accent/20 animate-float" style={{animationDelay: '2s'}}></div>
          <div className="absolute bottom-40 right-1/3 w-20 h-20 rounded-full bg-primary/30 animate-float" style={{animationDelay: '3s'}}></div>
        </div>
        
        <div className="relative z-10 text-center animate-fade-up max-w-2xl mx-auto">
          <div className="mb-12">
            <div className="relative mb-8">
              <h1 className="text-9xl md:text-[12rem] font-bold gradient-text mb-4 leading-none">
                404
              </h1>
              <div className="absolute inset-0 text-9xl md:text-[12rem] font-bold text-primary/10 blur-sm leading-none">
                404
              </div>
            </div>
            
            <div className="glass-effect rounded-2xl p-8 mb-8 animate-scale-in">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 gradient-text">
                Oeps! Pagina niet gevonden
              </h2>
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                De pagina die je zoekt lijkt verdwaald te zijn in de digitale ruimte. 
                Geen zorgen, we helpen je graag weer op het juiste pad.
              </p>
              
              <div className="w-20 h-1 bg-gradient-primary mx-auto rounded-full mb-6"></div>
              
              <p className="text-sm text-muted-foreground/80">
                Mogelijk is de link verouderd of heb je een typfout gemaakt
              </p>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-in">
            <Button asChild size="lg" className="group hover-lift shadow-elegant">
              <Link to="/">
                <Home className="mr-2 h-5 w-5 group-hover:scale-110 transition-transform" />
                Terug naar home
              </Link>
            </Button>
            
            <Button 
              variant="outline" 
              size="lg" 
              onClick={() => window.history.back()} 
              className="group hover-lift border-primary/20 hover:border-primary/40"
            >
              <ArrowLeft className="mr-2 h-5 w-5 group-hover:-translate-x-2 transition-transform duration-300" />
              Vorige pagina
            </Button>
          </div>
          
          <div className="mt-12 pt-8 border-t border-border/50">
            <p className="text-sm text-muted-foreground mb-4">Of ontdek onze diensten:</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Button variant="ghost" size="sm" asChild className="hover:bg-primary/10">
                <Link to="/organisatieopstellingen">Organisatieopstellingen</Link>
              </Button>
              <Button variant="ghost" size="sm" asChild className="hover:bg-primary/10">
                <Link to="/teamcoaching">Teamcoaching</Link>
              </Button>
              <Button variant="ghost" size="sm" asChild className="hover:bg-primary/10">
                <Link to="/over">Over Marthe</Link>
              </Button>
              <Button variant="ghost" size="sm" asChild className="hover:bg-primary/10">
                <Link to="/contact">Contact</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NotFound;
